import React, { useEffect, useRef } from "react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";

import "./pluginStyles.css";
import { getCookie } from "./pluginScript";

type PluginRowData = Record<string, unknown>;

// the quote-swapped variant is lossy at the source, so a row whose own value
// contains apostrophes in structural positions cannot always be recovered - fall
// back to showing the payload as text instead of leaving the click dead
type PluginRowModal = { rows: PluginRowData } | { raw: string };

// jQuery UI is not loaded in the react app, but legacy plugin templates still
// call `.dialog()` to build their "additional details" popup. Without a stub the
// call throws and aborts the rest of the plugin script (data table bindings
// included), so keep it chainable and no-op - the popup itself is rendered by
// react from the `.viewPluginRowData` click below.
const ensureDialogShim = () => {
  const jq = (window as any).jQuery;
  if (jq?.fn && !jq.fn.dialog) {
    jq.fn.dialog = function () {
      return this;
    };
  }
};

const BASE64_PATTERN = /^[A-Za-z0-9+/]+={0,2}$/;

// most templates encode the row with `btoa(JSON.stringify(row))`, which is
// latin1, but some backends emit base64 of utf-8 bytes - try utf-8 first and
// fall back to the raw binary string so both decode.
const decodeBase64 = (encoded: string) => {
  const binary = window.atob(encoded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return binary;
  }
};

// the other template variant skips base64 and stores
// `JSON.stringify(row).replace(/"/g, "'")` raw in the attribute. Swapping every
// quote back - what the legacy handler did - corrupts any value holding an
// apostrophe, so only swap the quotes sitting in a structural position and keep
// the rest as text. Literal control characters are escaped on the way through,
// since JSON.parse rejects them inside a string.
const singleToDoubleQuotedJson = (raw: string) => {
  const CONTROL_ESCAPES: Record<string, string> = {
    "\n": "\\n",
    "\r": "\\r",
    "\t": "\\t",
  };
  let out = "";
  let inString = false;
  let escaped = false;

  for (let i = 0; i < raw.length; i++) {
    const char = raw[i];

    if (inString && escaped) {
      // `\"` in the stringified row became `\'`, so put the quote back
      out += char === "'" ? '"' : char;
      escaped = false;
      continue;
    }

    if (inString && char === "\\") {
      out += char;
      escaped = true;
      continue;
    }

    if (char === "'") {
      if (!inString) {
        inString = true;
        out += '"';
        continue;
      }
      // a closing quote is only ever followed by a structural character
      let next = i + 1;
      while (next < raw.length && /\s/.test(raw[next])) next++;
      if (raw[next] === undefined || ":,}]".includes(raw[next])) {
        inString = false;
        out += '"';
      } else {
        out += "'";
      }
      continue;
    }

    out += inString ? CONTROL_ESCAPES[char] || char : char;
  }

  return out;
};

// `data-encoded` carries the row in one of several shapes depending on the
// plugin template, so try each in turn instead of assuming base64
const decodePluginRowData = (encoded: string): PluginRowData | null => {
  const raw = encoded.trim();
  const shapes = [
    // base64 of the json row
    () =>
      BASE64_PATTERN.test(raw) && raw.length % 4 === 0 ? decodeBase64(raw) : "",
    // plain json - getAttribute already un-escapes any &quot; in the markup
    () => raw,
    // json with its quotes swapped for apostrophes
    () => singleToDoubleQuotedJson(raw),
    // last resort: the blind swap the legacy handler used
    () => raw.replace(/'/g, '"'),
  ];

  for (const toJson of shapes) {
    try {
      const parsed = JSON.parse(toJson());
      if (parsed && typeof parsed === "object") {
        return parsed as PluginRowData;
      }
    } catch {
      // shape did not apply, try the next one
    }
  }

  console.log("viewPluginRowData: could not decode data-encoded", raw);
  return null;
};

const formatPluginRowValue = (value: unknown) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
};

const PluginEnv: React.FC<{ htmlContent: TrustedHTML }> = React.memo(
  ({ htmlContent }) => {
    const envRef = useRef<HTMLDivElement>(null);
    const hasRendered = useRef(false);
    const [loaded, setLoaded] = React.useState(false);
    const [rowModal, setRowModal] = React.useState<PluginRowModal | null>(null);
    const [rowModalTitle, setRowModalTitle] =
      React.useState("Additional details");

    useEffect(() => {
      const cssUrls = [
        "/vendors/font-awesome/css/font-awesome.min.css",
        "/vendors/datatables/datatables.min.css",
        "/vendors/toastr/toastr.min.css",
      ];

      // check if css already exists
      cssUrls.forEach((url) => {
        if (
          document.querySelector(
            `link[href="${process.env.STATIC_PATH + url}"]`,
          )
        ) {
          return;
        }

        const style = document.createElement("link");
        style.href = process.env.STATIC_PATH + url;
        style.rel = "stylesheet";
        document.head.appendChild(style);
      });

      return () => {
        cssUrls.forEach((url) => {
          const script = document.querySelector(
            `link[href="${process.env.STATIC_PATH + url}"]`,
          );
          if (script) {
            document.head.removeChild(script);
          }
        });
      };
    }, []);

    useEffect(() => {
      // const scriptUrl = process.env.STATIC_PATH + "/js/jquery.min.js"
      const scriptUrls = [
        "/js/jquery.min.js",
        // "/vendors/jquery1.13.2/jquery-ui.min.js",
        "/vendors/perfect-scrollbar/js/perfect-scrollbar.js",
        "/vendors/jquery-validate/jquery.validate.min.js",
        "/vendors/datatables/datatables.min.js",
        "/vendors/bootstrap5/bootstrap5.min.js", // replace this with 4.6.2
        "/vendors/toastr/toastr.min.js",
      ];

      let pendingCount = 0;
      // check if script already exists
      scriptUrls.forEach((url) => {
        if (
          document.querySelector(
            `script[src="${process.env.STATIC_PATH + url}"]`,
          )
        ) {
          return;
        }
        pendingCount++;
        // create script tag and append to body

        const script = document.createElement("script");
        script.src = process.env.STATIC_PATH + url;
        script.onload = function () {
          pendingCount--;
          if (pendingCount === 0) {
            setLoaded(true);
            sessionStorage.setItem("scriptsLoadedFlag", "true");
          }
        };
        document.body.appendChild(script);
      });

      if (pendingCount === 0) {
        setLoaded(true);
        sessionStorage.setItem("scriptsLoadedFlag", "true");
      }

      return () => {
        scriptUrls.forEach((url) => {
          const script = document.querySelector(
            `script[src="${process.env.STATIC_PATH + url}"]`,
          );
          if (script) {
            document.body.removeChild(script);
          }
        });
      };
    }, []);

    useEffect(() => {
      // wait until the dom has the script tag loaded earlier
      // create script tag and append to body
      hasRendered.current = false;
      if (htmlContent && loaded) {
        ensureDialogShim();

        const uniqueIds: string[] = [];

        // check if ref has script tag
        const frameId = requestAnimationFrame(() => {
          if (
            !envRef.current ||
            envRef.current.querySelectorAll("script").length === 0
          ) {
            return;
          }

          const pluginScript = envRef.current.querySelectorAll("script");

          pluginScript.forEach((scr) => {
            const script = document.createElement("script");
            const uniqueId =
              Date.now().toString() + Math.random().toString(36).substring(7);
            script.setAttribute("data-id", uniqueId);
            uniqueIds.push(uniqueId);

            if (scr && scr.src != "") {
              script.src = scr.src;
              script.async = true;
              script.defer = true;
            } else {
              script.textContent = scr.textContent;
            }
            document.body.appendChild(script);
            hasRendered.current = true;
          });
        });

        return () => {
          // check if script exists before removing using uniqueId
          cancelAnimationFrame(frameId);
          uniqueIds.forEach((uniqueId) => {
            const script = document.querySelector(
              `script[data-id="${uniqueId}"]`,
            );
            if (script) {
              document.body.removeChild(script);
            }
          });
        };
      }
    }, [htmlContent, loaded]);

    // legacy templates bind `.viewPluginRowData` clicks themselves and open a
    // jQuery UI dialog, which never works here. Handle the click on the
    // container in the capture phase - it runs before any jQuery handler bound
    // on the table below, so nothing the legacy handler does can pre-empt us -
    // and stop the event there so the legacy handler never runs at all.
    useEffect(() => {
      const container = envRef.current;
      if (!container) return;

      const handleRowDataClick = (event: MouseEvent) => {
        const target = event.target as HTMLElement | null;
        const trigger = target?.closest?.(
          ".viewPluginRowData",
        ) as HTMLElement | null;
        if (!trigger || !container.contains(trigger)) return;
        event.preventDefault();
        // keep the legacy jQuery handler (and its jQuery UI dialog, on setups
        // where jquery-ui is loaded) from running on top of the react modal
        event.stopPropagation();

        const encoded = trigger.getAttribute("data-encoded");
        if (!encoded) return;

        const decoded = decodePluginRowData(encoded);

        // legacy popup markup carries the heading in its title attribute
        const legacyModal = container.querySelector('[id$="info_modal"]');
        setRowModalTitle(
          legacyModal?.getAttribute("title") || "Additional details",
        );
        setRowModal(decoded ? { rows: decoded } : { raw: encoded.trim() });
      };

      container.addEventListener("click", handleRowDataClick, true);

      return () => {
        container.removeEventListener("click", handleRowDataClick, true);
      };
    }, [htmlContent, loaded]);

    useEffect(() => {
      // append getCookie to window objeetc
      (window as any).getCookie = getCookie;

      return () => {
        delete (window as any).getCookie;
      };
    }, []);

    return (
      <>
        <div
          ref={envRef}
          className="w-full plugin-style-container"
          dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
        <Dialog
          open={!!rowModal}
          onOpenChange={(open) => {
            if (!open) setRowModal(null);
          }}
        >
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>{rowModalTitle}</DialogTitle>
            </DialogHeader>
            <div className="max-h-[500px] overflow-y-auto">
              {rowModal && "rows" in rowModal ? (
                <table className="w-full table-fixed text-sm border border-border">
                  <thead>
                    <tr className="text-left">
                      <th className="w-[30%] p-2 border-b border-border">
                        Attribute
                      </th>
                      <th className="p-2 border-b border-border">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(rowModal.rows).map(([key, value]) => (
                      <tr key={key} className="align-top">
                        <td className="p-2 font-semibold break-words border-b border-border">
                          {key}
                        </td>
                        <td className="p-2 break-all border-b border-border">
                          {formatPluginRowValue(value)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <pre className="text-sm whitespace-pre-wrap break-all">
                  {rowModal && "raw" in rowModal ? rowModal.raw : ""}
                </pre>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  },
);

PluginEnv.displayName = "PluginEnv";
export default PluginEnv;
